import React, { useState, useEffect } from 'react';
import { Search, Link, Unlink, Building2, TrendingUp, Info, Loader } from 'lucide-react';
import { integrationsApi, grataApi } from '../services/api';
import './GrataIntegration.css';

export default function GrataIntegration({ currentUser }) {
  const [connection, setConnection] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [similarQuery, setSimilarQuery] = useState('');
  const [similarResults, setSimilarResults] = useState([]);
  const [enrichQuery, setEnrichQuery] = useState('');
  const [enrichResult, setEnrichResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('search');

  useEffect(() => {
    fetchConnection();
  }, []);

  async function fetchConnection() {
    try {
      const { data } = await integrationsApi.list();
      const grata = data.find(i => i.provider === 'grata');
      setConnection(grata || null);
    } catch (e) {
      console.error('Failed to load Grata connection', e);
    }
  }

  async function handleConnect(e) {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await integrationsApi.connectApiKey({ provider: 'grata', apiKey: apiKey.trim() });
      setApiKey('');
      await fetchConnection();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to connect Grata');
    } finally {
      setLoading(false);
    }
  }

  async function handleDisconnect() {
    setLoading(true);
    try {
      await integrationsApi.disconnect('grata');
      setConnection(null);
      setSearchResults([]);
      setSimilarResults([]);
      setEnrichResult(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to disconnect');
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch(e) {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await grataApi.search(searchQuery.trim());
      setSearchResults(data.results || data.companies || data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSimilar(e) {
    e.preventDefault();
    if (!similarQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await grataApi.similar(similarQuery.trim());
      setSimilarResults(data.results || data.companies || data.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Similar search failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleEnrich(e) {
    e.preventDefault();
    if (!enrichQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await grataApi.enrich(enrichQuery.trim());
      setEnrichResult(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Enrichment failed');
    } finally {
      setLoading(false);
    }
  }

  const isConnected = connection?.status === 'connected';

  return (
    <div className="grata-page">
      <div className="grata-header">
        <div className="grata-title-row">
          <Search size={22} />
          <div>
            <h1>Grata</h1>
            <span className="grata-subtitle">
              {isConnected
                ? 'Connected — search 21M+ private companies'
                : 'Connect your Grata account to search private market intelligence'}
            </span>
          </div>
        </div>
        <div className="grata-header-actions">
          {isConnected ? (
            <button className="grata-btn-secondary" onClick={handleDisconnect} disabled={loading}>
              <Unlink size={14} /> Disconnect
            </button>
          ) : (
            <span className="grata-status-badge">Not Connected</span>
          )}
        </div>
      </div>

      {!isConnected && (
        <div className="grata-connect-card">
          <div className="grata-connect-info">
            <Info size={18} />
            <p>
              Grata is a private market intelligence platform covering 21M+ companies.
              Enter your Grata API key to enable search, similar company discovery, and enrichment inside DealNexa.
            </p>
          </div>
          <form onSubmit={handleConnect} className="grata-connect-form">
            <input
              type="password"
              placeholder="Grata API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="grata-input"
            />
            <button type="submit" className="grata-btn-primary" disabled={loading || !apiKey.trim()}>
              {loading ? <Loader size={14} className="grata-spin" /> : <Link size={14} />}
              Connect Grata
            </button>
          </form>
        </div>
      )}

      {error && <div className="grata-error">{error}</div>}

      {isConnected && (
        <>
          <div className="grata-tabs">
            <button className={activeTab === 'search' ? 'active' : ''} onClick={() => setActiveTab('search')}>
              <Search size={14} /> Search
            </button>
            <button className={activeTab === 'similar' ? 'active' : ''} onClick={() => setActiveTab('similar')}>
              <Building2 size={14} /> Similar Companies
            </button>
            <button className={activeTab === 'enrich' ? 'active' : ''} onClick={() => setActiveTab('enrich')}>
              <TrendingUp size={14} /> Enrich
            </button>
          </div>

          {activeTab === 'search' && (
            <div className="grata-tab-content">
              <form onSubmit={handleSearch} className="grata-search-form">
                <input
                  type="text"
                  placeholder="Search private companies (e.g. 'HVAC roll-up targets Southeast $5-20M revenue')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="grata-search-input"
                />
                <button type="submit" className="grata-btn-primary" disabled={loading}>
                  {loading ? <Loader size={14} className="grata-spin" /> : <Search size={14} />}
                  Search
                </button>
              </form>
              <GrataResults results={searchResults} loading={loading} />
            </div>
          )}

          {activeTab === 'similar' && (
            <div className="grata-tab-content">
              <form onSubmit={handleSimilar} className="grata-search-form">
                <input
                  type="text"
                  placeholder="Grata Company ID (e.g. 'valdosta-family-medicine')"
                  value={similarQuery}
                  onChange={(e) => setSimilarQuery(e.target.value)}
                  className="grata-search-input"
                />
                <button type="submit" className="grata-btn-primary" disabled={loading}>
                  {loading ? <Loader size={14} className="grata-spin" /> : <Building2 size={14} />}
                  Find Similar
                </button>
              </form>
              <GrataResults results={similarResults} loading={loading} />
            </div>
          )}

          {activeTab === 'enrich' && (
            <div className="grata-tab-content">
              <form onSubmit={handleEnrich} className="grata-search-form">
                <input
                  type="text"
                  placeholder="Grata Company ID to enrich"
                  value={enrichQuery}
                  onChange={(e) => setEnrichQuery(e.target.value)}
                  className="grata-search-input"
                />
                <button type="submit" className="grata-btn-primary" disabled={loading}>
                  {loading ? <Loader size={14} className="grata-spin" /> : <TrendingUp size={14} />}
                  Enrich
                </button>
              </form>
              {enrichResult && (
                <div className="grata-enrich-card">
                  <pre>{JSON.stringify(enrichResult, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function GrataResults({ results, loading }) {
  if (loading && results.length === 0) {
    return <div className="grata-empty">Searching Grata...</div>;
  }
  if (results.length === 0) {
    return <div className="grata-empty">No results yet. Run a search above.</div>;
  }
  return (
    <div className="grata-results">
      {results.map((company, idx) => (
        <div key={company.id || company.grata_id || idx} className="grata-result-card">
          <div className="grata-result-name">{company.name || company.company_name || 'Unknown'}</div>
          <div className="grata-result-meta">
            {company.headquarters && <span>{company.headquarters}</span>}
            {company.industry && <span>{company.industry}</span>}
            {company.revenue && <span>Revenue: {company.revenue}</span>}
            {company.employees && <span>Employees: {company.employees}</span>}
          </div>
          {company.description && (
            <div className="grata-result-desc">{company.description}</div>
          )}
        </div>
      ))}
    </div>
  );
}
